import {
  PROMO_COMPRAOBTIENES,
  PROMO_MASXMENOS,
  PROMO_REGULAR,
  PROMO_ESCALONADA,
  PROMO_RULETA
} from "@/constants/constantes_varias";

import { NEGOCIOS } from "~/constants/negocios";
import { computed, ref, watch } from "vue";

export function useProductPricing(product, quantity) {
  const modo_sistema = useState("modo_sistema");

  const precio_unitario = ref(0);
  const precio_unitario_anterior = ref(0);
  const precio_dividido = ref(0);
  const precio_total = ref(0);
  const currentPromotionType = ref("");
  const CUOTAS = 6;

  // Estado visual para promo MAS X MENOS (se mantiene de tu otra rama)
  const masxmenos_precio_final = ref(0);
  const masxmenos_label = ref("");
  const is_masxmenos_activo = ref(false);

  const normalizarNumero = (valor) => {
    const n = Number(valor);
    return Number.isFinite(n) ? n : 0;
  };

  const resetMasXMenosState = () => {
    is_masxmenos_activo.value = false;
    masxmenos_precio_final.value = 0;
    masxmenos_label.value = "";
  };

  const obtenerPrecioBase = () => {
    const p = product?.value || {};

    const precio_venta = normalizarNumero(p.precio_venta);
    const precio_venta_web = normalizarNumero(p.precio_venta_web);

    // Regla por negocio:
    // - Baterías: preferir precio_venta
    // - Neumáticos: preferir precio_venta_web
    if (modo_sistema.value === NEGOCIOS.BATERIAS) {
      return precio_venta > 0 ? precio_venta : precio_venta_web;
    }

    if (modo_sistema.value === NEGOCIOS.NEUMATICOS) {
      return precio_venta_web > 0 ? precio_venta_web : precio_venta;
    }

    // Fallback defensivo
    if (precio_venta_web > 0) return precio_venta_web;
    if (precio_venta > 0) return precio_venta;
    return precio_venta_web || precio_venta || 0;
  };

  const calcularPrecioTotalSinDescuento = () => {
    const q = normalizarNumero(quantity?.value);

    if (precio_unitario_anterior.value > 0) {
      return precio_unitario_anterior.value * q;
    }

    return precio_total.value;
  };

  const calcularPrecioTotal = () => {
    if (is_masxmenos_activo.value) {
      return masxmenos_precio_final.value;
    }

    const q = normalizarNumero(quantity?.value);
    return precio_unitario.value * q;
  };

  const calcularPrecioDividido = () => {
    return precio_total.value / CUOTAS;
  };

  const selectPromotion = (promotionType, promotion) => {
    currentPromotionType.value = promotionType;
    calculatePrices(promotion);
  };

  const calculateOldPrice = () => {
    const markup = normalizarNumero(product?.value?.markup);

    if (markup === 0) {
      return 0;
    }

    const precioVenta = obtenerPrecioBase();
    const divisor = 1 - markup / 100;

    if (divisor <= 0) {
      return 0;
    }

    return precioVenta / divisor;
  };

  const calculateDiscountedPrice = (discountPercentage) => {
    const precioBase = obtenerPrecioBase();
    const markup = normalizarNumero(product?.value?.markup);

    if (!discountPercentage) return precioBase;

    if (markup === 0) {
      return precioBase * (1 - discountPercentage / 100);
    }

    const divisor = 1 - markup / 100;
    if (divisor <= 0) return precioBase;

    return (precioBase / divisor) * (1 - discountPercentage / 100);
  };

  // Mantiene la lógica visual mas x menos, pero usando precio base por negocio (no hardcodeado a web)
  const calcularMasPorMenosPrecio = (promotion) => {
    const precioBase = obtenerPrecioBase();

    if (!promotion) {
      resetMasXMenosState();
      return precioBase;
    }

    if (quantity.value === promotion.cantidad_x) {
      // Total promocional del pack (ej: lleva Y pagando X? depende de tu regla actual)
      // Aquí preservamos tu diseño visual de total separado:
      const precioFinal = precioBase * normalizarNumero(promotion.cantidad_y) + 1;

      is_masxmenos_activo.value = true;
      masxmenos_precio_final.value = precioFinal;
      masxmenos_label.value = `${promotion.cantidad_x}x${promotion.cantidad_y}`;

      // El unitario se mantiene base; el total visual lo controla masxmenos_precio_final
      return precioBase;
    }

    resetMasXMenosState();
    return precioBase;
  };

  const calcularMasPorMenosPrecioViejo = (promotion) => {
    if (!promotion) return 0;
    if (quantity.value !== promotion.cantidad_x) return 0;

    const precioBase = obtenerPrecioBase();
    // Total sin promo para mostrar tachado en modo mas x menos
    return precioBase * normalizarNumero(promotion.cantidad_x);
  };

  const calculateSteppedPrice = (promotion) => {
    const precioBase = obtenerPrecioBase();
    const markup = normalizarNumero(product?.value?.markup);

    if (!promotion) return precioBase;

    const calcularConPorcentaje = (porcentaje) => {
      if (markup === 0) {
        return precioBase * (1 - porcentaje / 100);
      }

      const divisor = 1 - markup / 100;
      if (divisor <= 0) return precioBase;

      return (precioBase / divisor) * (1 - porcentaje / 100);
    };

    switch (quantity.value) {
      case promotion.cantidad_1:
        return calcularConPorcentaje(promotion.porcentaje_1);
      case promotion.cantidad_2:
        return calcularConPorcentaje(promotion.porcentaje_2);
      case promotion.cantidad_3:
        return calcularConPorcentaje(promotion.porcentaje_3);
      case promotion.cantidad_4:
        return calcularConPorcentaje(promotion.porcentaje_4);
      case promotion.cantidad_5:
        return calcularConPorcentaje(promotion.porcentaje_5);
      default:
        return precioBase;
    }
  };

  const calculatePrices = (promotion) => {
    const promo = promotion || { tipo: "" };

    // Reset por defecto si no es MAS X MENOS
    if (promo.tipo !== PROMO_MASXMENOS) {
      resetMasXMenosState();
    }

    precio_unitario_anterior.value = calculateOldPrice();

    switch (promo.tipo) {
      case PROMO_REGULAR:
        precio_unitario.value = calculateDiscountedPrice(promo.porcentaje);
        break;

      case PROMO_MASXMENOS:
        precio_unitario.value = calcularMasPorMenosPrecio(promo);
        precio_unitario_anterior.value = calcularMasPorMenosPrecioViejo(promo);
        break;

      case PROMO_ESCALONADA:
        precio_unitario.value = calculateSteppedPrice(promo);
        break;

      default:
        precio_unitario.value = obtenerPrecioBase();
        break;
    }

    precio_total.value = calcularPrecioTotal();
    precio_dividido.value = calcularPrecioDividido();
  };

  const onQuantityChange = () => {
    const promociones = Array.isArray(product?.value?.promociones)
      ? product.value.promociones
      : [];

    if (promociones.length === 0) {
      resetMasXMenosState();
      precio_total.value = calcularPrecioTotal();
      precio_dividido.value = calcularPrecioDividido();
      return;
    }

    const moreForLessPromotionIndex = promociones.findIndex(
      (promo) => promo.tipo === PROMO_MASXMENOS
    );
    if (moreForLessPromotionIndex !== -1) {
      selectPromotion(PROMO_MASXMENOS, promociones[moreForLessPromotionIndex]);
    }

    const steppedPromotionIndex = promociones.findIndex(
      (promo) => promo.tipo === PROMO_ESCALONADA
    );
    if (steppedPromotionIndex !== -1) {
      selectPromotion(PROMO_ESCALONADA, promociones[steppedPromotionIndex]);
    }

    precio_total.value = calcularPrecioTotal();
    precio_dividido.value = calcularPrecioDividido();
  };

  const onProductChange = (producto_value = {}) => {
    const promociones = Array.isArray(producto_value.promociones) ? producto_value.promociones : [];
    const promotionTypes = [PROMO_REGULAR, PROMO_MASXMENOS, PROMO_ESCALONADA];

    if (
      promociones.length === 0 ||
      (
        promociones.length === 1 &&
        (
          promociones[0]?.tipo === PROMO_COMPRAOBTIENES ||
          promociones[0]?.tipo === PROMO_RULETA
        )
      )
    ) {
      calculatePrices({ tipo: "" });
      return;
    }

    const promocionesConPrecio = promociones.filter(
      (promo) =>
        promo.tipo !== PROMO_COMPRAOBTIENES &&
        promo.tipo !== PROMO_RULETA
    );

    if (promocionesConPrecio.length === 0) {
      calculatePrices({ tipo: "" });
      return;
    }

    for (const promotionType of promotionTypes) {
      const promotionIndex = promocionesConPrecio.findIndex(
        (promo) => promo.tipo === promotionType
      );

      if (promotionIndex !== -1) {
        selectPromotion(promotionType, promocionesConPrecio[promotionIndex]);
        return;
      }
    }

    // Fallback defensivo
    calculatePrices({ tipo: "" });
  };

  watch(precio_unitario, () => {
    precio_total.value = calcularPrecioTotal();
    precio_dividido.value = calcularPrecioDividido();
  });

  return {
    precio_unitario: computed(() => precio_unitario.value),
    precio_unitario_anterior: computed(() => precio_unitario_anterior.value),
    precio_total_sin_descuento: computed(() => calcularPrecioTotalSinDescuento()),
    precio_dividido: computed(() => precio_dividido.value),
    precio_total: computed(() => precio_total.value),
    onProductChange,
    onQuantityChange,

    is_masxmenos_activo: computed(() => is_masxmenos_activo.value),
    masxmenos_precio_final: computed(() => masxmenos_precio_final.value),
    masxmenos_label: computed(() => masxmenos_label.value),
  };
}